import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Building2, Users, BookOpen, UserCheck } from "lucide-react";

const ROLE_ICONS = {
  mentor: Users,
  tutor: BookOpen,
  partner_institution: Building2,
  employee: UserCheck,
  intern: UserCheck,
};

const ROLE_LABELS = {
  mentor: "Mentor",
  tutor: "Tutor / LMS Author",
  partner_institution: "Partner Institution",
  employee: "Hired candidate",
  intern: "Hired candidate",
};

// Sub-roles admin can pick for hired employees. Free-form on the backend so
// we can add new ones without a schema change.
const EMPLOYEE_SUB_ROLES = [
  { key: "intern", label: "Intern" },
  { key: "part_time_employee", label: "Part-time Employee" },
  { key: "full_time_employee", label: "Full-time Employee" },
  { key: "contractor", label: "Contractor" },
];

export default function AdminRoleApprovalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [subRole, setSubRole] = useState({}); // per-user
  const [team, setTeam] = useState({});
  const [orgUnit, setOrgUnit] = useState({});
  const [department, setDepartment] = useState({});
  const [orgUnits, setOrgUnits] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users/pending-verification");
      setItems(r.data);
      // Prime the sub-role picker so admins can approve with 1 click.
      const seed = {};
      (r.data || []).forEach((u) => {
        if (u.role === "employee" || u.role === "intern") seed[u.user_id] = u.employee_role || "intern";
      });
      setSubRole(seed);
    } catch (e) { toast.error("Could not load pending list"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/org/units").then((r) => setOrgUnits(r.data || [])).catch(() => {});
  }, []);

  const decide = async (u, approved) => {
    setBusy(u.user_id);
    try {
      const payload = { approved, note: notes[u.user_id] || "" };
      if (approved && (u.role === "employee" || u.role === "intern")) {
        payload.role = "employee";
        payload.employee_role = subRole[u.user_id] || "intern";
        payload.is_intern = (subRole[u.user_id] === "intern");
        if (team[u.user_id]) payload.team_section = team[u.user_id];
        if (orgUnit[u.user_id]) payload.org_unit_id = orgUnit[u.user_id];
        if (department[u.user_id]) payload.department = department[u.user_id];
      }
      await api.post(`/admin/users/${u.user_id}/verify`, payload);
      toast.success(approved ? "User approved — they can now access their dashboard" : "User rejected");
      await load();
    } catch (e) { toast.error("Decision failed"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-role-approvals-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ROLE APPROVALS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Approve <em className="italic text-kb-primary">privileged accounts</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Mentors, tutors and partner institutions self-register but cannot access the workspace until you verify their credentials.
        Approve to unlock; reject to block (they&apos;ll see a rejected status on sign-in).
      </p>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted" data-testid="role-approvals-empty">
            No pending approvals — you&apos;re all caught up.
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {items.map((u) => {
              const Icon = ROLE_ICONS[u.role] || Users;
              return (
                <div key={u.user_id} className="kb-card p-6" data-testid={`approval-${u.user_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-kb-primary/10 rounded-sm flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-lg text-kb-ink">{u.name}</div>
                        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-0.5">{u.email}</div>
                        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary mt-2">{ROLE_LABELS[u.role]}</div>
                        {u.affiliation && (
                          <div className="text-sm text-kb-ink-muted mt-1">Affiliation: <span className="text-kb-ink">{u.affiliation}</span></div>
                        )}
                        {u.academic_level && (
                          <div className="text-sm text-kb-ink-muted">Academic level: <span className="text-kb-ink capitalize">{u.academic_level}</span></div>
                        )}
                        {u.domains?.length > 0 && (
                          <div className="text-xs text-kb-ink-muted mt-1">Domains: {u.domains.join(", ")}</div>
                        )}
                        <div className="kb-mono text-[10px] text-kb-ink-light mt-2">
                          Registered {new Date(u.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 pt-5 border-t border-slate-200">
                    {(u.role === "employee" || u.role === "intern") && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-testid={`approval-${u.user_id}-hire-fields`}>
                        <div>
                          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Role</label>
                          <select
                            className="kb-input text-sm mt-1"
                            value={subRole[u.user_id] || "intern"}
                            onChange={(e) => setSubRole({ ...subRole, [u.user_id]: e.target.value })}
                            data-testid={`approval-${u.user_id}-subrole`}
                          >
                            {EMPLOYEE_SUB_ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Team (optional)</label>
                          <input
                            className="kb-input text-sm mt-1"
                            placeholder="e.g. Research, ML, DevOps"
                            value={team[u.user_id] || ""}
                            onChange={(e) => setTeam({ ...team, [u.user_id]: e.target.value })}
                            data-testid={`approval-${u.user_id}-team`}
                          />
                        </div>
                        <div>
                          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Organisational unit (optional)</label>
                          <select
                            className="kb-input text-sm mt-1"
                            value={orgUnit[u.user_id] || ""}
                            onChange={(e) => setOrgUnit({ ...orgUnit, [u.user_id]: e.target.value })}
                            data-testid={`approval-${u.user_id}-org-unit`}
                          >
                            <option value="">— None —</option>
                            {orgUnits.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Department (optional)</label>
                          <input
                            className="kb-input text-sm mt-1"
                            placeholder="e.g. AI Research, Growth"
                            value={department[u.user_id] || ""}
                            onChange={(e) => setDepartment({ ...department, [u.user_id]: e.target.value })}
                            data-testid={`approval-${u.user_id}-department`}
                          />
                        </div>
                      </div>
                    )}
                    <input
                      className="kb-input text-sm"
                      placeholder="Verification note (optional, e.g. 'verified ORCID + h-index')"
                      value={notes[u.user_id] || ""}
                      onChange={(e) => setNotes({ ...notes, [u.user_id]: e.target.value })}
                      data-testid={`approval-${u.user_id}-note`}
                    />
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => decide(u, true)} disabled={busy === u.user_id}
                        className="kb-btn-primary text-sm" data-testid={`approval-${u.user_id}-approve`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve & grant access
                      </button>
                      <button onClick={() => decide(u, false)} disabled={busy === u.user_id}
                        className="kb-btn-outline text-sm" data-testid={`approval-${u.user_id}-reject`}>
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
